import { useEffect, useState } from 'preact/hooks';

/** Total walkthrough footage the CMA waits for, summed across every clip. */
export const MIN_TOTAL_VIDEO_SECONDS = 180;

export interface VideoClip {
  id: string;
  signedUrl?: string | null;
  /** Read by the server on upload; null when it could not read the clip's header. */
  durationSeconds?: unknown;
}

function serverDuration(video: VideoClip): number | undefined {
  return typeof video.durationSeconds === 'number' ? video.durationSeconds : undefined;
}

/**
 * Total length in seconds of the clips still on the form. Uses the length the server
 * read on upload (a just-uploaded clip has no signed URL yet), falling back to the
 * clip's metadata in the browser. A clip whose length is unknown adds nothing.
 */
export function useTotalVideoSeconds(videos: VideoClip[]): number {
  const [durations, setDurations] = useState<Record<string, number>>({});
  const key = videos.map((v) => v.id).join(',');

  useEffect(() => {
    const cleanups: (() => void)[] = [];
    for (const video of videos) {
      if (serverDuration(video) !== undefined || !video.signedUrl || durations[video.id] !== undefined) {
        continue;
      }
      const el = document.createElement('video');
      el.preload = 'metadata';
      const onLoaded = () => {
        if (Number.isFinite(el.duration)) {
          setDurations((prev) => ({ ...prev, [video.id]: el.duration }));
        }
      };
      el.addEventListener('loadedmetadata', onLoaded);
      el.src = video.signedUrl;
      cleanups.push(() => {
        el.removeEventListener('loadedmetadata', onLoaded);
        el.removeAttribute('src');
      });
    }
    return () => cleanups.forEach((fn) => fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Only clips still present: a deleted clip's entry stays in `durations` but no longer counts.
  return videos.reduce((sum, v) => sum + (serverDuration(v) ?? durations[v.id] ?? 0), 0);
}

export function formatMinutes(seconds: number): string {
  const whole = Math.floor(seconds);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
}
